import React, { useState, useEffect, useContext } from 'react';
import { AppBar, Toolbar, useMediaQuery, useTheme, Box, Typography, Snackbar, Tooltip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CustomToolbarButton from './toolbar/custom-toolbar-button';
import ASCopyIcon from '../../../assets/icons/Copy.svg';
import ASPrintIcon from '../../../assets/icons/Print.svg';
import ASReferencesIcon from '../../../assets/icons/References.svg';
import ApiIconButton from '../../UIControls/api-button/api-icon-button';
import SuccessDialog from '../../UIControls/api-button/SuccessDialog';
import ErrorDialog from '../../UIControls/api-button/ErrorDialog';
import ShareButton from './share-button';
import HandoutEmailButton from './handout-email-button';
import PatientDetailsDialog from './patient-details-dialog';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import mixpanel from 'mixpanel-browser';
import { useGenerateLink } from './use-generated-link';
import AddToBundleButton from '../../UIControls/AddToBundleButton';
import { toJS } from 'mobx';
import UserApi from '../../../rest-api/user-api';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import CTAUpgradeDialog from '../../UIControls/CTAUpgradeDialog';
import ctaUpgradeProfessionalPaywall from '../../../assets/banners/ctaUpgradeProfessionalPaywall.png';
import { getCTAUpgradeImageUrl } from '../../../config';
import { notifyAuthExpired, isAuthExpiredDialogVisible, isAuthExpiredError } from '../../../utils/auth-error-utils';

function HandoutActions({
    handoutData,
    onToggleAuthorsAndReferences,
    bundledHandoutsView = false,
    showLabels,
    show3DModel = true,
    showMainIllustration = true,
    showTutorialVideo = true,
    is3DViewerOpen = false,
    isDiagramOnly = false,
    is3DModelOnly = false,
    embedMode = false,
    allowAuthorsButton = true,
    showWhiteboard = false,
}) {
    const { authStore, userDataStore, whiteboardStore, customisedHandoutStore } = useContext(StoreContext);
    const lastWhiteboardState = localStorage.getItem('lastWhiteboardState');
    const whiteboardData = lastWhiteboardState ? JSON.parse(lastWhiteboardState) : null;
    const { generateLink, generateLinkOnly, createShareData, shouldShowPatientDialog } = useGenerateLink(
        handoutData,
        bundledHandoutsView,
        whiteboardData,
    );
    const navigate = useNavigate();
    const theme = useTheme();
    const [currentUser, setCurrentUser] = useState(null);
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarStatus, setSnackbarStatus] = useState('success');
    const [upgradeCTAOpen, setUpgradeCTAOpen] = useState(false);
    const [upgradeCTAClinicOpen, setUpgradeCTAClinicOpen] = useState(false);
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [pendingShareAction, setPendingShareAction] = useState(null); // 'copy', 'email', or 'share'
    const [showCopySuccessDialog, setShowCopySuccessDialog] = useState(false);
    const [copyErrorDialogOpen, setCopyErrorDialogOpen] = useState(false);
    const [copyErrorDialogTitle, setCopyErrorDialogTitle] = useState('Copy to clipboard error');
    const [copyErrorDialogMessage, setCopyErrorDialogMessage] = useState(
        'There was an error copying to the clipboard. Please try again.',
    );
    const [copyShowReloadAction, setCopyShowReloadAction] = useState(false);
    const toggleStates = { show3DModel, showMainIllustration, showTutorialVideo };

    const shouldShowAuthorsButton = embedMode
        ? allowAuthorsButton
        : !bundledHandoutsView && !is3DViewerOpen && !isDiagramOnly && !is3DModelOnly;

    useEffect(() => {
        if (embedMode) return;
        const fetchCurrentUser = async () => {
            try {
                const user = await Auth.currentAuthenticatedUser();
                setCurrentUser(user);
            } catch (error) {
                console.error('Error fetching current user: ', error);
            }
        };
        fetchCurrentUser();
    }, []);

    const openCopyErrorDialog = (error) => {
        const isAuthExpired = isAuthExpiredError(error);
        if (isAuthExpired) {
            notifyAuthExpired({ source: 'copy-link' });
            return;
        }
        setCopyErrorDialogTitle('Copy to clipboard error');
        setCopyErrorDialogMessage('There was an error copying to the clipboard. Please try again.');
        setCopyShowReloadAction(false);
        setCopyErrorDialogOpen(true);
    };

    const handlePatientDetailsSubmit = async (patientDetails) => {
        // Called after patient dialog is submitted
        // Execute the pending share action with patient details
        setPatientDialogOpen(false);
        try {
            if (pendingShareAction === 'copy') {
                await executeCopyLink(patientDetails);
                // Show success dialog after copy completes
                setShowCopySuccessDialog(true);
            } else if (pendingShareAction === 'print') {
                await executePrintPDF(patientDetails);
            }
        } catch (error) {
            console.error('Error executing share action:', error);
            if (pendingShareAction === 'copy') {
                openCopyErrorDialog(error);
            }
        } finally {
            // Reset pending action
            setPendingShareAction(null);
        }
    };

    const executeCopyLink = async (patientDetails = null) => {
        // Generate link with patient details
        const linkId = await generateLink(patientDetails);
        const userId = userDataStore.userId;
        const url = new URL(process.env.REACT_APP_PATIENT_HANDOUT_URL);
        url.searchParams.append('linkId', linkId);
        url.searchParams.append('clinicId', userId);

        // Add toggle states
        const { show3DModel, showMainIllustration, showTutorialVideo } = toggleStates;
        if (show3DModel === false) url.searchParams.append('show3DModel', 'false');
        if (showMainIllustration === false) url.searchParams.append('showMainIllustration', 'false');
        if (showTutorialVideo === false) url.searchParams.append('showTutorialVideo', 'false');

        const link = url.toString();

        try {
            // Attempt to copy using Clipboard API
            await navigator.clipboard.writeText(link);
            trackHandoutShared('Copy handout');
        } catch (error) {
            console.warn('Clipboard API failed, using fallback method.', error);
            // Fallback for Safari
            const textArea = document.createElement('textarea');
            textArea.value = link;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                const successful = document.execCommand('copy');
                if (!successful) {
                    throw new Error('Fallback copy command failed');
                }
                trackHandoutShared('Copy handout');
            } catch (error) {
                console.error('Fallback method failed: ', error);
                alert(
                    `Safari browser has limitations for accessing the clipboard. Please copy the following link manually to share with your patient: \n\n${link}`,
                );
            }
            document.body.removeChild(textArea);
        }
    };

    const handleCopyLink = async () => {
        if (isAuthExpiredDialogVisible()) {
            const authDialogError = new Error('AUTH_EXPIRED_DIALOG_OPEN');
            authDialogError.isExpected = true;
            throw authDialogError;
        }

        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            setUpgradeCTAOpen(true);
            // Throw special error to prevent ApiIconButton from showing error dialog
            const upgradeError = new Error('UPGRADE_REQUIRED');
            upgradeError.isExpected = true;
            throw upgradeError;
        }

        // Check if we should show patient dialog (My Library content)
        if (shouldShowPatientDialog()) {
            setPendingShareAction('copy');
            setPatientDialogOpen(true);
            // Throw special error to prevent ApiIconButton from showing success dialog
            // Success will be shown manually after patient details are submitted
            const pendingDialogError = new Error('PATIENT_DIALOG_PENDING');
            pendingDialogError.isExpected = true;
            throw pendingDialogError;
        } else {
            // My Clinic content - generate link directly
            await executeCopyLink();
        }
    };

    const handlePrint = async () => {
        if (userDataStore?.subscriptionData?.accountType === 'Free') {
            setUpgradeCTAOpen(true);
            return;
        }

        if (userDataStore?.subscriptionData?.accountType === 'Student') {
            setUpgradeCTAOpen(true);
            return;
        }

        // Check if we should show patient dialog (My Library content)
        if (shouldShowPatientDialog()) {
            setPendingShareAction('print');
            setPatientDialogOpen(true);
            return;
        } else {
            // My Clinic content - generate PDF directly with patient details
            await executePrintPDF();
        }
    };

    const executePrintPDF = async (patientDetails = null) => {
        trackHandoutShared('Print Handout');

        // For My Clinic content, fetch patient details automatically
        if (!patientDetails && !shouldShowPatientDialog()) {
            try {
                // Check for customised handout state
                const customisedHandoutState = localStorage.getItem('lastCustomisedHandoutState');
                if (customisedHandoutState) {
                    const chState = JSON.parse(customisedHandoutState);
                    if (chState.customisedId && chState.patientDetails) {
                        patientDetails = chState.patientDetails;
                    }
                }

                // Check for whiteboard state
                if (!patientDetails) {
                    const whiteboardState = localStorage.getItem('lastWhiteboardState');
                    if (whiteboardState) {
                        const wbState = JSON.parse(whiteboardState);
                        if (wbState.whiteboardId && wbState.patientDetails) {
                            patientDetails = wbState.patientDetails;
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to load patient details from state:', error);
            }
        }

        handleViewHandoutPDF(patientDetails);
    };

    const handleViewHandoutPDF = (patientDetails = null) => {
        trackHandoutShared('View Handout PDF');
        const url = new URL(window.location.origin + `/handout/${handoutData.contentId}/pdf`);
        url.searchParams.append('showLabels', showLabels);

        let whiteboardId = null;
        let customisedId = null;

        // Check if handoutData itself is a customised handout (has customisedId property)
        if (handoutData.customisedId) {
            customisedId = handoutData.customisedId;
            url.searchParams.append('customisedId', customisedId);
        } else {
            // Fall back to localStorage for customised handout state
            const customisedHandoutState = localStorage.getItem('lastCustomisedHandoutState');
            if (customisedHandoutState) {
                try {
                    const chState = JSON.parse(customisedHandoutState);
                    if (chState.customisedId) {
                        customisedId = chState.customisedId;
                        url.searchParams.append('customisedId', customisedId);
                    }
                } catch (error) {
                    console.error('Failed to parse customised handout state:', error);
                }
            }
        }

        // Check if handoutData itself is a whiteboard (has whiteboardId property)
        if (handoutData.whiteboardId) {
            whiteboardId = handoutData.whiteboardId;
            url.searchParams.append('whiteboardId', whiteboardId);

            // Extract patient details from whiteboard if not provided
            if (!patientDetails && (handoutData.patientName || handoutData.patientSurname)) {
                patientDetails = {
                    patientName: handoutData.patientName,
                    patientSurname: handoutData.patientSurname,
                };
            }
        } else {
            // Fall back to localStorage for whiteboard state
            const whiteboardState = localStorage.getItem('lastWhiteboardState');
            if (whiteboardState) {
                try {
                    const wbState = JSON.parse(whiteboardState);
                    if (wbState.whiteboardId) {
                        whiteboardId = wbState.whiteboardId;
                        url.searchParams.append('whiteboardId', whiteboardId);
                    }
                } catch (error) {
                    console.error('Failed to parse whiteboard state:', error);
                }
            }
        }

        // If we have a whiteboardId but no patient details yet, try to get them from the store
        if (whiteboardId && !patientDetails && whiteboardStore?.whiteboardData) {
            const whiteboardObj = whiteboardStore.whiteboardData[whiteboardId];
            if (whiteboardObj && (whiteboardObj.patientName || whiteboardObj.patientSurname)) {
                patientDetails = {
                    patientName: whiteboardObj.patientName,
                    patientSurname: whiteboardObj.patientSurname,
                };
            }
        }

        // If we have a customisedId but no patient details yet, try to get them from the store
        if (customisedId && !patientDetails && customisedHandoutStore?.customisedHandoutData) {
            const customisedObj = customisedHandoutStore.customisedHandoutData[customisedId];
            if (customisedObj && (customisedObj.patientName || customisedObj.patientSurname)) {
                patientDetails = {
                    patientName: customisedObj.patientName,
                    patientSurname: customisedObj.patientSurname,
                };
            }
        }

        // Pass patient details via URL parameters if provided
        if (patientDetails) {
            if (patientDetails.patientName) {
                url.searchParams.append('patientName', patientDetails.patientName);
            }
            if (patientDetails.patientSurname) {
                url.searchParams.append('patientSurname', patientDetails.patientSurname);
            }
        }

        window.open(url.toString(), '_blank');
    };

    const trackHandoutShared = (shareMethod) => {
        mixpanel.track('HandoutShared', {
            email: currentUser?.attributes?.email,
            shareMethod: shareMethod,
            handoutTitle: handoutData.contentName,
        });
    };

    const flattenCategories = (categories) => {
        let result = [];
        function traverse(currentObject) {
            if (typeof currentObject === 'string') {
                result.push(currentObject);
            } else if (Array.isArray(currentObject)) {
                currentObject.forEach((item) => {
                    if (typeof item === 'object') {
                        traverse(item);
                    } else {
                        result.push(item);
                    }
                });
            } else {
                for (const key in currentObject) {
                    result.push(key);
                    traverse(currentObject[key]);
                }
            }
        }
        traverse(categories);
        return result.join(', ');
    };

    const handleAddToBundledHandouts = async () => {
        let isMultiUser = userDataStore?.subscriptionData?.accountType === 'Clinic-multiuser' ? true : false;
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student' ||
            userDataStore?.subscriptionData?.accountType === 'Professional'
        ) {
            setUpgradeCTAClinicOpen(true);
            return;
        }
        console.warn(toJS(handoutData));
        console.warn(toJS(userDataStore));

        // Handle case where handouts may be undefined
        const userHandoutsJson = userDataStore.userData.handouts || '[]';
        let userHandouts = JSON.parse(userHandoutsJson);
        let formattedCategories = flattenCategories(handoutData.categories);
        let existingContentIds = userHandouts.map((handout) => handout.contentId);

        // Check if contentId already exists to prevent duplicates
        if (existingContentIds.includes(handoutData.contentId)) {
            setSnackbarMessage('Handout already exists in the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        if (userHandouts.length >= 5) {
            setSnackbarMessage('Cannot add more than 5 handouts to the bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
            return;
        }

        userHandouts.push({
            contentId: handoutData.contentId,
            contentName: handoutData.contentName,
            categories: formattedCategories,
        });

        let payload = {
            handouts: userHandouts,
        };

        console.warn(payload);

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }

            // After successful API call, update the local user handouts
            userDataStore.updateUserHandouts(userHandouts);
            setSnackbarMessage('Handout added to bundle');
            setSnackbarStatus('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error adding handout to bundle: ', error);
            setSnackbarMessage('Failed to add handout to bundle');
            setSnackbarStatus('error');
            setSnackbarOpen(true);
        }
    };

    const handleShowUpgradePrompt = () => {
        if (
            userDataStore?.subscriptionData?.accountType === 'Free' ||
            userDataStore?.subscriptionData?.accountType === 'Student'
        ) {
            setUpgradeCTAOpen(true);
        }
    };

    const getSnackbarIcon = () => {
        return snackbarStatus === 'success' ? (
            <CheckIcon sx={{ ml: 1, color: 'white' }} />
        ) : (
            <CloseIcon sx={{ ml: 1, color: 'red' }} />
        );
    };

    return (
        <>
            <PatientDetailsDialog
                open={patientDialogOpen}
                onClose={() => {
                    setPatientDialogOpen(false);
                    setPendingShareAction(null);
                }}
                onSubmit={handlePatientDetailsSubmit}
            />
            <SuccessDialog
                open={showCopySuccessDialog}
                handleClose={() => setShowCopySuccessDialog(false)}
                title="Copy to clipboard successful!"
                message="The link was copied to the clipboard successfully."
            />
            <ErrorDialog
                open={copyErrorDialogOpen}
                handleClose={() => {
                    setCopyErrorDialogOpen(false);
                    setCopyShowReloadAction(false);
                }}
                title={copyErrorDialogTitle}
                message={copyErrorDialogMessage}
                primaryActionText={copyShowReloadAction ? 'Reload Page' : null}
                onPrimaryAction={copyShowReloadAction ? () => window.location.reload() : null}
            />
            <CTAUpgradeDialog
                open={upgradeCTAOpen}
                handleClose={() => {
                    setUpgradeCTAOpen(false);
                }}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Unlock unlimited sharing and more with just one click!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to enjoy full access!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts',
                    'Unlimited digital sharing of all anatomy diagrams',
                    'Personalise with your clinic details ',
                ]}
                imageUrl={ctaUpgradeProfessionalPaywall}
            />
            <CTAUpgradeDialog
                open={upgradeCTAClinicOpen}
                handleClose={() => setUpgradeCTAClinicOpen(false)}
                onConfirm={() => navigate('/settings/manage-subscription')}
                title="Upgrade now for clinic-wide access and advanced features!"
                message="You've reached a feature that's beyond your current plan. Upgrade today to add more users and enjoy full access to our features!"
                buttonText="Upgrade plan"
                benefits={[
                    'Unlimited digital sharing of all handouts & anatomy diagrams',
                    'Bundling of handouts and diagrams for seamless sharing',
                    'Personalise with your clinic logo',
                ]}
                imageUrl={getCTAUpgradeImageUrl('clinic')}
            />
            {!isTablet && (
                <Box
                    sx={{
                        width: '100%',
                        padding: bundledHandoutsView ? '0' : '0 40px',
                        height: 88,
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        '@media print': {
                            display: 'none',
                        },
                        backgroundColor: 'white',
                    }}
                    className="handout-actions-container"
                >
                    {!bundledHandoutsView ? (
                        !showWhiteboard ? (
                            <Tooltip title="Add handout to bundle" arrow>
                                <Box sx={{ cursor: 'pointer' }}>
                                    <AddToBundleButton onClick={handleAddToBundledHandouts} />
                                </Box>
                            </Tooltip>
                        ) : (
                            // Reserve the same space so the right-side actions don't shift
                            <Box aria-hidden sx={{ width: 200, height: 48 }} />
                        )
                    ) : null}

                    <Box sx={{ display: 'flex', gap: 3 }}>
                        {!bundledHandoutsView && !is3DModelOnly && (
                            <Box
                                textAlign="center"
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                            >
                                <Typography variant="body2" sx={{ color: '#FF669B', fontSize: 8 }}>
                                    Print
                                </Typography>
                                <CustomToolbarButton iconSrc={ASPrintIcon} tooltipTitle="Print" onClick={handlePrint} />
                            </Box>
                        )}
                        {!embedMode && (
                            <>
                                <Box
                                    textAlign="center"
                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                                >
                                    {/* variant="body2" changing to smaller size */}
                                    <Typography variant="body2" sx={{ color: '#FF669B', fontSize: 8 }}>
                                        Copy
                                    </Typography>
                                    <ApiIconButton
                                        showSuccessDialog={
                                            userDataStore?.subscriptionData?.accountType === 'Student' ||
                                            userDataStore?.subscriptionData?.accountType === 'Free'
                                                ? false
                                                : true
                                        }
                                        apiCall={handleCopyLink}
                                        onError={(error) => {
                                            // Don't show error dialog if patient dialog is pending or upgrade required
                                            if (
                                                error.message === 'PATIENT_DIALOG_PENDING' ||
                                                error.message === 'UPGRADE_REQUIRED' ||
                                                error.message === 'AUTH_EXPIRED_DIALOG_OPEN'
                                            ) {
                                                return true; // Silently ignore this error
                                            }
                                            // For other errors, let ApiIconButton handle them
                                            return false;
                                        }}
                                        icon={
                                            <CustomToolbarButton
                                                iconSrc={ASCopyIcon}
                                                tooltipTitle={bundledHandoutsView ? 'Copy handouts' : 'Copy handout'}
                                            />
                                        }
                                        successTitle="Copy to clipboard successful!"
                                        successMessage="The link was copied to the clipboard successfully."
                                        errorTitle="Copy to clipboard error"
                                        errorMessage="There was an error copying to the clipboard. Please try again."
                                        ariaLabel="Copy handout"
                                    />
                                </Box>
                                <Box
                                    textAlign="center"
                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                                >
                                    <Typography variant="body2" sx={{ color: '#FF669B', fontSize: 8 }}>
                                        Email
                                    </Typography>
                                    <HandoutEmailButton
                                        handoutData={handoutData}
                                        bundledHandoutsView={bundledHandoutsView}
                                        currentUser={currentUser}
                                        accountType={userDataStore?.subscriptionData?.accountType}
                                        onUpgradeRequired={handleShowUpgradePrompt}
                                        toggleStates={toggleStates} // Pass toggle states here
                                        generateLink={generateLink}
                                        shouldShowPatientDialog={shouldShowPatientDialog}
                                    />
                                </Box>
                                <Box
                                    textAlign="center"
                                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                                >
                                    <Typography variant="body2" sx={{ color: '#FF669B', fontSize: 8 }}>
                                        Share
                                    </Typography>
                                    <ShareButton
                                        generateLink={(patientDetails) => createShareData(toggleStates, patientDetails)}
                                        bundledHandoutsView={bundledHandoutsView}
                                        currentUser={currentUser}
                                        accountType={userDataStore?.subscriptionData?.accountType}
                                        onUpgradeRequired={handleShowUpgradePrompt}
                                        shouldShowPatientDialog={shouldShowPatientDialog}
                                    />
                                </Box>
                            </>
                        )}
                        {shouldShowAuthorsButton && (
                            <Box
                                textAlign="center"
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
                            >
                                <Typography variant="body2" sx={{ color: '#FF669B', fontSize: 8 }}>
                                    Authors
                                </Typography>
                                <CustomToolbarButton
                                    iconSrc={ASReferencesIcon}
                                    tooltipTitle="Authors and references"
                                    onClick={onToggleAuthorsAndReferences}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
            {isTablet && (
                <AppBar
                    position="fixed"
                    color="default"
                    sx={{
                        top: 'auto',
                        bottom: 0,
                        backgroundColor: 'white',
                        pr: 10,
                        '@media print': { display: 'none' },
                    }}
                >
                    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', height: 88 }}>
                        {!bundledHandoutsView ? (
                            !showWhiteboard ? (
                                <Box sx={{ cursor: 'pointer' }}>
                                    <AddToBundleButton onClick={handleAddToBundledHandouts} />
                                </Box>
                            ) : (
                                // Keep layout consistent when hidden
                                <Box aria-hidden sx={{ width: 200, height: 48 }} />
                            )
                        ) : null}
                        <Box>
                            <ShareButton
                                generateLink={(patientDetails) => createShareData(toggleStates, patientDetails)}
                                currentUser={currentUser}
                                accountType={userDataStore?.subscriptionData?.accountType}
                                onUpgradeRequired={handleShowUpgradePrompt}
                                shouldShowPatientDialog={shouldShowPatientDialog}
                            />

                            {!bundledHandoutsView && !is3DModelOnly && (
                                <CustomToolbarButton iconSrc={ASPrintIcon} tooltipTitle="Print" onClick={handlePrint} />
                            )}
                            {shouldShowAuthorsButton && (
                                <CustomToolbarButton
                                    iconSrc={ASReferencesIcon}
                                    tooltipTitle="Authors and references"
                                    onClick={onToggleAuthorsAndReferences}
                                />
                            )}
                        </Box>
                    </Toolbar>
                </AppBar>
            )}

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => {
                    setSnackbarOpen(false);
                }}
                message={
                    <Box display="flex" alignItems="center">
                        {snackbarMessage}
                        {getSnackbarIcon()}
                    </Box>
                }
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundImage: 'linear-gradient(132deg, #FA96C3 -2.07%, #FF669B 15.82%, #BD8DE8 116.64%)',
                        display: 'flex',
                        justifyContent: 'center',
                        borderRadius: 2,
                    },
                }}
            />
        </>
    );
}

export default HandoutActions;
